import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  Task,
  TaskUpdateInput,
  MaterialEquipmentOperationInput,
} from '@models/task.model';
import TasksGraphQL from '@graphql/task.queries';
import {
  graphQlClient,
  graphQlMultipleUpload,
  graphQlUpload,
} from '@config/graphqlClient';
import { formatDateForSubmission } from '@utils/formatDate';
import { normalizeRichTextForSubmit } from '@utils/richText';
import { useTranslation } from '@hooks/useTranslation';

interface UpdateTaskParams {
  taskId: string;
  name: string;
  description: string | null;
  taskType: string;
  // status: string | null;
  otherType: string | null;
  budget: number | null; // Add budget field
  manHours: number | null;
  startDate: Date | string;
  endDate: Date | string;
  timeConstraint: number | null;
  selectedTypeConstraint: string | null;
  crewIds?: string[];
  deleteDocuments?: string[];
  presedentTaskId?: string[];
  presedentEventId?: string[];
  documents?: File[] | null; // Changed from avatars to documents for consistency
  parentTaskId?: string | null;
  delegateOrganizationId?: string | null;
  rescheduledTask?: Task[];
  note?: string | null;
  superintendentId?: string | null;
  projectId: string | null;
  initialData: Task | null;
  delegateTaskId?: string | null;
  materialEquipmentOperations?: MaterialEquipmentOperationInput;
  subtasks?: {
    add: string[];
    remove: string[];
  };
}

interface UseUpdateTaskReturn {
  updateTask: (params: UpdateTaskParams) => Promise<Task | null>;
  loading: boolean;
}

// Update task function
const updateTaskAPI = async (params: UpdateTaskParams) => {
  const taskData: TaskUpdateInput = {
    taskId: params.taskId,
    name: params.name,
    // `|| undefined` here was a real bug: TypeORM skips undefined, so a user who cleared
    // the description could never actually erase it. An explicit null does clear the column.
    // Rich text makes it worse — an emptied editor emits a truthy `<p></p>`.
    description: normalizeRichTextForSubmit(params.description),
    taskType: params.taskType,
    otherType: params.otherType || undefined,
    budget: params.budget || undefined,
    manHours: params.manHours || undefined,
    startTime: formatDateForSubmission(params.startDate) || params.startDate,
    endTime: formatDateForSubmission(params.endDate) || params.endDate,
    timeConstraint: params.timeConstraint || undefined,
    typeOfTimeConstraint: params.selectedTypeConstraint || undefined,
    crewIds: params.crewIds,
    projectId: params.projectId || undefined,
    parentTaskId: params.parentTaskId || undefined,
    presedentTaskId: params.presedentTaskId,
    presedentEventId: params.presedentEventId,
    delegateOrganizationId: params.delegateOrganizationId || undefined,
    superintendentId: params.superintendentId || undefined,
    note: params.note || undefined,
    materialEquipmentOperations: params.materialEquipmentOperations,
    subtasks: params.subtasks,
  };

  const data =
    params.documents && params.documents.length > 0
      ? params.documents.length === 1
        ? await graphQlUpload(
            params.documents[0],
            'task',
            TasksGraphQL.mutations.updateTask,
            taskData,
          )
        : await graphQlMultipleUpload(
            params.documents,
            'task',
            TasksGraphQL.mutations.updateTask,
            taskData,
          )
      : await graphQlClient.request(TasksGraphQL.mutations.updateTask, {
          task: taskData,
        });

  return data;
};

export const useUpdateTask = (): UseUpdateTaskReturn => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: updateTaskAPI,
    onSuccess: (data, variables) => {
      if (data?.updateTask?.taskId) {
        toast.success(tActionMessages.taskUpdatedSuccessfully);

        // Invalidate related queries to refresh data
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });

        // Update the task in cache
        queryClient.setQueryData(['task', variables.taskId], data.updateTask);
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors[0]?.message || 'Error updating task';
      toast.error(errorMessage);
    },
  });

  const updateTask = async (params: UpdateTaskParams): Promise<Task | null> => {
    try {
      const result = await mutation.mutateAsync(params);
      return result?.updateTask || null;
    } catch (error) {
      return null;
    }
  };

  return {
    updateTask,
    loading: mutation.isPending,
  };
};
